import React, { HTMLAttributes } from 'react';
import { Dropdown, DropdownProps } from '../Dropdown/Dropdown';
import styles from './ShareDropdown.module.scss';
import { SocialMediaType, socials, SocialMediaShareData } from 'utils/socials';
import { ShareDropdownSection } from './ShareDropdownSection';
import { removeNestingWith } from 'utils/array';
import { LinkIcon } from 'icons/link';

export type CopyableOption = {
  type: 'copy';
  toCopy: string;
  label: string;
  icon?: React.ReactElement;
};

/**
 * A generic payload representing a thing that can be shared.
 * On unsupported devices, this is rendered as an `<a>` link, on devices
 * that support {@link navigator.share} it is rendered as an `onClick` handler.
 */
export type SharableOption = {
  type: 'share';
  platform: SocialMediaType;
  /**
   * Desktop devices can choose between specific types of copy to share to specific
   * targets, but that's not possible for native. We want to include both in here
   * so we can make that decision when we're about to render the click handler.
   */
  sharePayload: ScopedSocialSharingData;
  label: string;
  icon?: React.ReactElement;
  options?: Partial<HTMLAttributes<HTMLInputElement>>;
};

export type DropdownOption = SharableOption | CopyableOption;

export function ShareDropdown({ sections, name, ...rest }: ShareDropdownProps) {
  // We want to represent lines in between sections by using a nested array
  // to make sure we can't create invalid state (2 line breaks after each other for example).
  // We then flatten the nested array representation into a regular array where
  // the spaces between the nested arrays are turned into a line break under the hood
  const parts = removeNestingWith<DropdownOption | { type: 'line' }>(
    { type: 'line' as const },
    sections,
  );

  return (
    <Dropdown {...rest}>
      <div className={styles.shareDropdown}>
        <h2 className={styles.title}>{name}</h2>
        <div className={styles.shareDropdownGrid}>
          {parts.map((option, j) =>
            option.type === 'line' ? (
              <Separator key={j} />
            ) : (
              <ShareDropdownSection option={option} key={j} />
            ),
          )}
        </div>
      </div>
    </Dropdown>
  );
}

function Separator() {
  return <hr className={styles.separator} />;
}

export const toCopyLink = (toCopy: string): CopyableOption => ({
  type: 'copy',
  label: 'Copy link',
  toCopy,
  icon: <LinkIcon />,
});

/**
 * A type representing sharing copy overrides for specific platforms.
 * Always has a `fallback` key.
 * @example
 * ```ts
 * {
 *    twitter: { text: "Look at our @product" },
 *    fallback: { text: "Look at our product here <link>" }
 * }
 * ```
 */
export type ScopedSocialSharingData = Partial<Record<SocialMediaType, SocialMediaShareData>> & {
  /**
   * Anything not deserving of a specific message (like twitter) will
   * render with the fallback message.
   */
  fallback: SocialMediaShareData;
};

export const toSocialMediaURL = (
  socialMediaType: SocialMediaType,
  scoped: ScopedSocialSharingData,
): URL => {
  const { sharingLink } = socials[socialMediaType];
  return sharingLink(scoped[socialMediaType] ?? scoped.fallback);
};

export const toSharable = (
  socialMediaTypes: SocialMediaType[],
  socialData: ScopedSocialSharingData,
): SharableOption[] => {
  return socialMediaTypes.map((type) => {
    const { displayName, icon } = socials[type];
    return {
      type: 'share',
      platform: type,
      sharePayload: socialData,
      label: displayName,
      icon: icon,
    };
  });
};

export type ShareDropdownProps = DropdownProps & {
  name: string;
  /**
   * Nested arrays are separated by a line break in a generic
   * way without enforcing the order of copy/link type options
   */
  sections: DropdownOption[][];
};
